/**
 * Стартовый экран и сравнение тарифов.
 *
 * Первое, что видит новый браузер. Раньше на его месте сразу открывался
 * вопрос про доход — без объяснения, куда человек попал, и без явного входа
 * для тех, у кого аккаунт уже есть.
 *
 * Здесь ровно то, что нужно для выбора пути: что человек получит, честная
 * цена входа, кнопка начала и вход. Каруселей, видео, установки приложения и
 * запроса карты нет — до первой пользы просить нечего.
 *
 * «Посмотреть пример» и «Free и Pro» убраны (решение владельца 22.10.2026): у
 * экрана одна задача — начать. Пример на чужих числах ничего не объяснял, а
 * тарифы до первой пользы читаются как «здесь заплатят». Цены — на лендинге и
 * на экране Pro, там, где Pro нужен; экран тарифов остался по адресу /plans.
 *
 * Картинка занимает экран целиком, и логотип с обещанием нарисованы прямо в
 * ней. Поэтому своей шапки у экрана нет: вторая надпись «SKOKA» поверх той,
 * что уже есть на снимке, — это не бренд, а сбой вёрстки.
 *
 * Три строки под заголовком называют цену входа, и каждая проверяема: пять
 * вопросов (доход, счета, платежи, жизнь, цели — шестой шаг прогресса это уже
 * сам план), первая цифра после второго вопроса, аккаунт и карта не нужны до
 * результата. Обещать здесь можно только то, что человек увидит сам через
 * минуту, — иначе первое же обещание продукта оказывается ложным.
 */
import { useEffect, useState, type CSSProperties } from 'react';
import { Wordmark } from './logo.js';
import { Icon } from './icon.js';

/**
 * Какой из двух светлых снимков показать.
 *
 * Чередуются по очереди, а не случайно: человек, вернувшийся на старт второй
 * раз, видит другую картинку, и это ощущается как живое приложение, а не как
 * сломанный кэш. Счётчик живёт в localStorage, и его отсутствие ничего не
 * ломает — тогда показывается первый.
 */
const KEY = 'skoka.start.shot';
function nextShot(): 1 | 2 {
  try {
    const seen = Number(localStorage.getItem(KEY) ?? '0');
    const shot = seen % 2 === 0 ? 1 : 2;
    localStorage.setItem(KEY, String(seen + 1));
    return shot;
  } catch {
    return 1;
  }
}

export function Welcome({ onStart, onLogin }: {
  onStart: () => void;
  onLogin: () => void;
}) {
  const [shot, setShot] = useState<1 | 2>(1);
  // Снимок выбирается при показе, а не при сборке: иначе один и тот же файл
  // оставался бы в разметке навсегда.
  useEffect(() => { setShot(nextShot()); }, []);

  return (
    <div className="start">
      <div className="startshot" style={{ '--shot': `url(/start-light-${shot}.jpg)` } as CSSProperties} />
      {/* Вход — в шапке: он нужен вернувшемуся, и искать его внизу длинного
          экрана ему незачем. Новый человек читает дальше. */}
      <div className="startbar"><button className="btn ghost sm" onClick={onLogin}>Войти</button></div>
      <main className="startmain">
        <div className="startcard">
          <h1 className="welcome-h1">Понятные деньги<br />для твоих целей</h1>
          <p className="startlead">
            Поможем понять, сколько денег доступно, что уже отложено и как
            двигаться к целям.
          </p>

          <ul className="facts">
            <li><QuestionIcon /> Видите полную картину ваших финансов</li>
            <li><ClockIcon /> План без стресса</li>
            <li><CardIcon /> Подсказки и проверка покупок</li>
            <li><PiggyIcon /> Накопления и цели</li>
          </ul>

          <button className="btn pri" onClick={onStart}>Начать бесплатно</button>
          {/*
            Обещание входа названо до того, как человек начал.

            Регистрация стоит после плана: сначала польза, потом аккаунт. Но
            умолчать об этом нельзя — человек, вложивший десять минут и
            упёршийся в неожиданную форму, справедливо считает это обманом.
          */}
          <p className="startnote">
            Сначала посчитаем твои деньги. Регистрация понадобится, чтобы их сохранить.
          </p>

        </div>
      </main>
    </div>
  );
}

const QuestionIcon = () => <Icon name="circle-question" size={16} />;
const ClockIcon = () => <Icon name="clock" size={16} />;
const CardIcon = () => <Icon name="credit-card" size={16} />;
const PiggyIcon = () => <Icon name="piggy-bank" size={16} />;

/**
 * Сравнение Free и Pro до регистрации.
 *
 * Открывается по ссылке со старта, ничего не запускает и аккаунт не создаёт.
 * Цены и объёмы — решения владельца 21.10.2026 (docs/design/pro-2026-10-21).
 */
export function Plans({ onBack }: { onBack: () => void }) {
  return (
    <div className="phone">
      <div className="bgart" />
      <main>
        <div className="brandbar">
          <Wordmark />
          <button className="lnk" onClick={onBack}>Назад</button>
        </div>
        <h1 className="welcome-h1">Free и Pro</h1>

        <section className="blk">
          <div className="blkhead"><b>Free</b></div>
          <div className="li"><div className="nm"><b>Полная картина денег</b>
            <span>Счета, доходы, долги и все платежи с любой периодичностью — без лимитов</span>
          </div></div>
          <div className="li"><div className="nm"><b>Первый ИИ-разбор</b>
            <span>Бесплатно, без карты</span>
          </div></div>
          <div className="li"><div className="nm"><b>Проверка покупки</b>
            <span>Считает приложение, а не модель, — поэтому без ограничений</span>
          </div></div>
          <div className="li"><div className="nm"><b>Запись операций вручную</b>
            <span>Сумма, категория и дата — сколько угодно записей, без подписки</span>
          </div></div>
          <div className="li"><div className="nm"><b>Цели, календарь и предупреждения</b>
            <span>Назначение будущего дохода на платежи и цели</span>
          </div></div>
          <div className="li"><div className="nm"><b>Распределение прихода</b>
            <span>Пересчёт после каждого прихода и раскладка вручную — без подписки</span>
          </div></div>
        </section>

        <section className="blk">
          <div className="blkhead"><b>Pro</b>
            <span className="tag soft">от 249 ₽ в месяц</span></div>
          <div className="li"><div className="nm"><b>Голосом и словами</b>
            <span>«Кофе 250 с карты» — сумма, категория и счёт уже заполнены. 200 записей в месяц</span>
          </div></div>
          <div className="li"><div className="nm"><b>Чеки и выписки</b>
            <span>Фото чека, файл или скриншот из банка — операции без ручного ввода. 40 в месяц</span>
          </div></div>
          <div className="li"><div className="nm"><b>Спроси SKOKA</b>
            <span>Вопросы про свои деньги и свежий разбор в любой день. 30 вопросов в месяц</span>
          </div></div>
          <div className="li"><div className="nm"><b>Аналитика словами</b>
            <span>Что видно за период и разбор прошлого периода: что выросло и что это даст целям</span>
          </div></div>
        </section>

        <section className="blk">
          <div className="blkhead"><b>Сколько стоит</b></div>
          <div className="li"><div className="nm"><b>1 месяц — 399 ₽</b><span>каждый месяц</span></div></div>
          <div className="li"><div className="nm"><b>3 месяца — 990 ₽</b><span>330 ₽ в месяц</span></div></div>
          <div className="li"><div className="nm"><b>6 месяцев — 1 790 ₽</b><span>298 ₽ в месяц</span></div></div>
          <div className="li"><div className="nm"><b>12 месяцев — 2 990 ₽</b>
            <span>249 ₽ в месяц. Новым пользователям — первый год за 1 495 ₽, если оплатить до 31 декабря 2026</span></div></div>
        </section>

        <div className="note">
          Учёт, «Можно тратить», предупреждения о нехватке денег, платежи, цели, графики,
          записи вручную и первый разбор оплатой не закрываются никогда. Объёмы обновляются каждый месяц в число оплаты.
          Автопродление выключено, пока не включишь сам; передумал — вернём деньги за
          неиспользованные дни. Пробный период — 7 дней, без карты.
        </div>
        <button className="btn" onClick={onBack}>Назад</button>
      </main>
    </div>
  );
}
